import type { DashLanguages } from "#i18n/utils";

const BEAMER_PRODUCT_ID = process.env.BEAMER_PRODUCT_ID;

export const BEAMER_SELECTOR_ID = "beamer-news-icon";

export interface BeamerUser {
	id: string;
	email?: string | null;
	name?: string | null;
	language: DashLanguages;
}

let loadPromise: Promise<void> | null = null;

const openListeners = new Set<() => void>();

export function subscribeBeamerOpen(listener: () => void) {
	openListeners.add(listener);
	return () => {
		openListeners.delete(listener);
	};
}

function toBeamerLanguage(language: DashLanguages): string {
	if (language.startsWith("pt")) return "PT";
	if (language.startsWith("es")) return "ES";
	if (language.startsWith("fr")) return "FR";
	return "EN";
}

function splitName(name?: string | null): { firstname?: string; lastname?: string } {
	if (!name) return {};
	const [firstname, ...rest] = name.trim().split(/\s+/);
	return { firstname, lastname: rest.join(" ") || undefined };
}

function identityParams(user: BeamerUser): Partial<BeamerConfig> {
	const { firstname, lastname } = splitName(user.name);
	return {
		user_id: user.id,
		user_email: user.email ?? undefined,
		user_firstname: firstname,
		user_lastname: lastname,
		language: toBeamerLanguage(user.language),
	};
}

function buildConfig(user: BeamerUser): BeamerConfig {
	return {
		product_id: BEAMER_PRODUCT_ID,
		selector: `#${BEAMER_SELECTOR_ID}`,
		lazy: false,
		alert: true,
		...identityParams(user),
		onopen: () => {
			openListeners.forEach(listener => listener());
		},
	};
}

function loadBeamerScript(): Promise<void> {
	if (!BEAMER_PRODUCT_ID) return Promise.resolve();
	if (window.Beamer) return Promise.resolve();
	if (loadPromise) return loadPromise;

	loadPromise = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.id = "beamer-embed";
		script.src = "https://app.getbeamer.com/js/beamer-embed.js";
		script.onload = () => resolve();
		script.onerror = () => {
			loadPromise = null;
			window.beamer_config = undefined;
			reject(new Error("Beamer script failed to load"));
		};
		document.head.appendChild(script);
	});

	return loadPromise;
}

function updateBeamerIdentity(user: BeamerUser): void {
	const params = identityParams(user);
	if (window.beamer_config) {
		Object.assign(window.beamer_config, params);
	}
	window.Beamer?.update(params);
}

export async function initBeamer(user: BeamerUser): Promise<void> {
	if (!BEAMER_PRODUCT_ID) return;

	if (window.beamer_config) {
		updateBeamerIdentity(user);
		return;
	}

	window.beamer_config = buildConfig(user);

	await loadBeamerScript();
}

export function updateBeamer(): void {
	if (!BEAMER_PRODUCT_ID || !window.Beamer) return;
	window.Beamer.update();
}
